import { useState } from 'react';

import { Button, Icons, Stack, Typography } from '@cast/design-system';
import { ClusterOnboardingPathEnum } from '@cast/types';

import { AgentCodeSnippet } from 'components/code';
import { InlineCode } from 'components/InlineCode';
import { Link } from 'components/router';
import { createPermissionsCenterUrl } from 'features-op/permissions-center/navigation';
import { useIsReliabilityMetricsAvailableInBeta } from 'hooks/permissions/useIsReliabilityMetricsAvailableInBeta';
import { useConnectAndEnableCastAiScriptQuery } from 'hooks/queries/cluster/useConnectAndEnableCastAiScriptQuery';

import { CASTCTL_PLACEHOLDER_CLUSTER_PROVIDER } from './castctlConstants';
import {
  InstallCastctlCommand,
  InstallCastctlOperatingSystemToggle,
  InstallStepRow,
  type OperatingSystem,
} from './InstallCastctlSteps';
import { CastctlFeatureSelection } from './types';

type Props = {
  featureSelection: CastctlFeatureSelection;
};

export const InstructionsSection = ({ featureSelection }: Props) => {
  const isReliabilityMetricsAvailableInBeta =
    useIsReliabilityMetricsAvailableInBeta();
  const [operatingSystem, setOperatingSystem] =
    useState<OperatingSystem>('macos');
  const { script, isLoading, isFetching, error, refetch } =
    useConnectAndEnableCastAiScriptQuery({
      clusterProvider: CASTCTL_PLACEHOLDER_CLUSTER_PROVIDER,
      installWorkloadAutoscaler: featureSelection.workloadAutoscaler,
      installAutoscalerAgent: featureSelection.autoscaler,
      installReliabilityMetrics:
        isReliabilityMetricsAvailableInBeta &&
        featureSelection.reliabilityMetrics,
      installGPUPlugin: false,
      installSecurityAgent: false,
      installSecurityCompliance: false,
      installSecurityImageScanning: false,
      installNetflowExporter: false,
      installGpuMetricsExporter: false,
      installOmni: false,
      installAiOptimizerProxy: false,
      installPodMutator: false,
      onboardingPath: ClusterOnboardingPathEnum.CASTCTL,
    });

  return (
    <Stack gap={8}>
      <Typography variant="P12R" color="textSecondary">
        Your selected features are pre-configured.
      </Typography>

      <Stack>
        <InstallStepRow
          number={1}
          title="Install castctl"
          description="Skip this step if castctl is already installed."
        >
          <Stack gap={8}>
            <InstallCastctlOperatingSystemToggle
              value={operatingSystem}
              onChange={setOperatingSystem}
            />
            <InstallCastctlCommand operatingSystem={operatingSystem} />
          </Stack>
        </InstallStepRow>

        <InstallStepRow
          number={2}
          isLast
          title="Connect your cluster"
          description={
            <>
              Run from a terminal where <InlineCode>kubectl</InlineCode> context
              is set to the cluster you want to connect.
            </>
          }
        >
          <Stack gap={8}>
            <AgentCodeSnippet
              code={script}
              isLoading={isLoading || isFetching}
              error={error}
              refetch={refetch}
              testId="castctl-connect-script"
            />
            <Button
              component={Link}
              target="_blank"
              variant="text"
              endIcon={<Icons.ArrowSquareOut size={14} weight="regular" />}
              to={createPermissionsCenterUrl()}
              testId="permissions-center-link"
            >
              Cloud permissions details
            </Button>
          </Stack>
        </InstallStepRow>
      </Stack>
    </Stack>
  );
};
